import React, { useState, useRef, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Alert,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemText,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

// Icons
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

// Services
import {
  scanQRCode,
  markQRCodeAsUsed,
} from '../services/api';

// Auth context
import { useAuth } from '../contexts/AuthContext';

// QR Code detection library
import jsQR from 'jsqr';

// Types
import { QRCodeScanResult, QRCodePayload } from '../models/types';

// QR Code Scanner component using browser camera
const QRCodeScannerPage: React.FC = () => {
  const theme = useTheme();
  const { volunteer } = useAuth(); // Get current user from auth context
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const intervalRef = useRef<number | null>(null);

  // State
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState<QRCodeScanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [manualInput, setManualInput] = useState('');
  const [processing, setProcessing] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [markingAsUsed, setMarkingAsUsed] = useState(false);
  const [showManualInput, setShowManualInput] = useState(false);

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      stopScanning();
    };
  }, []);

  const setupVideoStream = (stream: MediaStream) => {
    // Add debugging for videoRef
    console.log('Setting up video stream...');
    console.log('videoRef:', videoRef);
    console.log('videoRef.current:', videoRef.current);
    console.log('videoRef.current exists:', !!videoRef.current);

    if (!videoRef.current) {
      console.error('Video element not found! videoRef.current is null');
      // Try again after a short delay
      setTimeout(() => {
        if (videoRef.current && streamRef.current) {
          setupVideoStream(streamRef.current);
        } else {
          console.error('Video element still not found after retry');
          setError('Video-Element konnte nicht gefunden werden. Bitte versuchen Sie es erneut.');
          setIsScanning(false);
        }
      }, 200);
      return;
    }

    const video = videoRef.current;
    console.log('Video element found:', video);
    console.log('Video element tagName:', video.tagName);
    console.log('Video element in DOM:', document.contains(video));
    
    // First, ensure the video element is ready
    console.log('Setting up video element...');
    console.log('Video element exists:', !!video);
    console.log('Stream tracks:', stream.getTracks().map(track => ({ kind: track.kind, label: track.label, enabled: track.enabled })));
    
    // Set the stream
    video.srcObject = stream;
    
    // Force video attributes before setting up event handlers
    video.muted = true;
    video.playsInline = true;
    video.autoplay = false; // We'll control playback manually
    video.defaultMuted = true;
    
    console.log('Video attributes set, adding event handlers...');
    
    // Set up event handlers
    const handleLoadedMetadata = () => {
      console.log('Video metadata loaded, dimensions:', video.videoWidth, 'x', video.videoHeight);
      console.log('Video readyState:', video.readyState);
      console.log('Video paused:', video.paused);
      console.log('Video muted:', video.muted);
      console.log('Video srcObject:', video.srcObject);
      
      // Only proceed if we have valid dimensions
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        console.log('Starting video playback...');
        const playPromise = video.play();
        
        if (playPromise !== undefined) {
          playPromise.then(() => {
            console.log('Video started playing successfully');
            console.log('Video current time:', video.currentTime);
            console.log('Video playback rate:', video.playbackRate);
            console.log('Video ready state:', video.readyState);
            
            setTimeout(() => {
              console.log('Starting scan loop...');
              console.log('Video still playing:', !video.paused);
              console.log('Video element visible:', video.offsetWidth > 0 && video.offsetHeight > 0);
              scanLoop();
            }, 500);
          }).catch((error) => {
            console.error('Error playing video:', error);
            // Try to play without sound
            video.muted = true;
            video.play().then(() => {
              console.log('Video started playing after muting');
              setTimeout(() => scanLoop(), 500);
            }).catch((err) => {
              console.error('Failed to play video even when muted:', err);
              setError('Video konnte nicht gestartet werden.');
            });
          });
        } else {
          console.log('Play promise is undefined, video might already be playing');
          setTimeout(() => scanLoop(), 500);
        }
      } else {
        console.log('Video dimensions not ready yet, will try again...');
      }
    };

    const handleCanPlay = () => {
      console.log('Video can play, dimensions:', video.videoWidth, 'x', video.videoHeight);
      console.log('Video duration:', video.duration);
    };

    const handlePlay = () => {
      console.log('Video play event fired');
    };

    const handleTimeUpdate = () => {
      // Only log occasionally to avoid spam
      if (Math.floor(video.currentTime) % 2 === 0) {
        console.log('Video time update:', video.currentTime);
      }
    };

    const handleError = (e: Event) => {
      console.error('Video error event:', e);
      const target = e.target as HTMLVideoElement;
      if (target && target.error) {
        console.error('Video error details:', target.error);
      }
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('play', handlePlay);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('error', handleError);
    
    console.log('Event listeners added, video properties set');
    console.log('Video srcObject set to:', video.srcObject);
    console.log('Video readyState after setting srcObject:', video.readyState);
    
    // Try to force loading
    setTimeout(() => {
      console.log('Force loading video...');
      video.load();
      
      setTimeout(() => {
        console.log('After load() - Video readyState:', video.readyState);
        if (video.readyState >= 1) {
          console.log('Video ready after load(), calling handleLoadedMetadata');
          handleLoadedMetadata();
        }
      }, 100);
    }, 100);
    
    // Also try to start immediately if metadata is already loaded
    if (video.readyState >= 1) {
      console.log('Video already ready, starting immediately');
      handleLoadedMetadata();
    } else {
      console.log('Video not ready yet, waiting for loadedmetadata event');
    }
    
    // Fallback: try to force load after a short delay
    setTimeout(() => {
      console.log('Fallback check - Video readyState:', video.readyState);
      console.log('Fallback check - Video dimensions:', video.videoWidth, 'x', video.videoHeight);
      
      if (video.readyState >= 1 && video.videoWidth > 0 && video.videoHeight > 0 && video.paused) {
        console.log('Fallback: starting video playback');
        video.play().then(() => {
          console.log('Fallback: Video started successfully');
          setTimeout(() => {
            if (!intervalRef.current) {
              console.log('Fallback: Starting scan loop');
              scanLoop();
            }
          }, 200);
        }).catch((error) => {
          console.error('Fallback: Error playing video:', error);
        });
      }
    }, 2000);
  };

  // Effect to setup video stream when scanning starts
  useEffect(() => {
    if (isScanning && streamRef.current && videoRef.current) {
      console.log('Video element now available, setting up stream...');
      setupVideoStream(streamRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScanning]);

  const startScanning = async () => {
    try {
      setError(null);
      
      // Try different video constraints for better compatibility
      const constraints = {
        video: {
          facingMode: 'environment', // Use back camera on mobile
          width: { min: 640, ideal: 1280, max: 1920 },
          height: { min: 480, ideal: 720, max: 1080 }
        },
        audio: false
      };

      console.log('Requesting camera access...');
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('Camera access granted, stream:', stream);

      // Store the stream and set scanning state
      // The useEffect will handle setting up the video when the element is available
      streamRef.current = stream;
      setIsScanning(true);
    } catch (error) {
      console.error('Error accessing camera:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      setError(`Kamera konnte nicht geöffnet werden: ${errorMessage}`);
    }
  };

  const stopScanning = () => {
    console.log('Stopping scanning...');
    
    // Clear animation frame
    if (intervalRef.current) {
      cancelAnimationFrame(intervalRef.current);
      intervalRef.current = null;
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        console.log('Stopping track:', track.kind);
        track.stop();
      });
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      const video = videoRef.current;
      video.srcObject = null;
      
      // Clean up all event listeners
      video.removeEventListener('loadedmetadata', () => {});
      video.removeEventListener('canplay', () => {});
      video.removeEventListener('play', () => {});
      video.removeEventListener('timeupdate', () => {});
      
      video.pause();
    }
    
    setIsScanning(false);
    console.log('Scanning stopped');
  };

  const scanLoop = () => {
    if (!isScanning || !videoRef.current || !canvasRef.current) {
      console.log('Scan loop stopped - missing refs or not scanning');
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) {
      console.error('Could not get canvas context');
      if (isScanning) {
        requestAnimationFrame(scanLoop);
      }
      return;
    }

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      try {
        // Set canvas dimensions to match video
        if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          console.log('Canvas dimensions set to:', canvas.width, 'x', canvas.height);
        }
        
        // Draw video frame to canvas
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Use jsQR to detect QR codes
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        
        if (code) {
          console.log('QR Code detected:', code.data);
          stopScanning();
          processQRCode(code.data);
          return;
        }
      } catch (error) {
        console.error('Error in scan loop:', error);
      }
    } else {
      console.log('Video not ready, readyState:', video.readyState, 'paused:', video.paused);
    }

    // Continue scanning
    if (isScanning) {
      intervalRef.current = requestAnimationFrame(scanLoop);
    }
  };

  const processQRCode = async (qrData: string) => {
    try {
      setProcessing(true);
      setError(null);

      const result = await scanQRCode(qrData);
      setScanResult(result);

      // Always show confirmation dialog if QR code is found and valid, regardless of usage status
      if (result.success && result.qrCode) {
        setConfirmDialogOpen(true);
      }
    } catch (error) {
      console.error('Error processing QR code:', error);
      setError('Fehler beim Verarbeiten des QR-Codes.');
    } finally {
      setProcessing(false);
    }
  };

  const handleManualInput = async () => {
    if (!manualInput.trim()) return;
    await processQRCode(manualInput.trim());
  };

  const handleMarkAsUsed = async () => {
    if (!scanResult?.qrCode || !volunteer?.name?.trim()) return;

    try {
      setMarkingAsUsed(true);
      await markQRCodeAsUsed(scanResult.qrCode.id, volunteer.name.trim());
      
      // Refresh scan result
      const updatedResult = await scanQRCode(scanResult.qrCode.qrCodePayload);
      setScanResult(updatedResult);
      setConfirmDialogOpen(false);
    } catch (error) {
      console.error('Error marking QR code as used:', error);
      setError('Fehler beim Markieren des QR-Codes als verwendet.');
    } finally {
      setMarkingAsUsed(false);
    }
  };

  const resetScanner = () => {
    setScanResult(null);
    setError(null);
    setManualInput('');
    setConfirmDialogOpen(false);
    setShowManualInput(false);
  };

  const renderScanResult = () => {
    if (!scanResult) return null;

    if (!scanResult.success) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            <ErrorIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Ungültiger QR-Code
          </Typography>
          <Typography variant="body2">
            {scanResult.error || 'QR-Code konnte nicht verarbeitet werden.'}
          </Typography>
        </Alert>
      );
    }

    const { qrCode, volunteer } = scanResult;
    if (!qrCode || !volunteer) return null;

    const qrPayload: QRCodePayload = JSON.parse(qrCode.qrCodePayload);
    
    if (scanResult.alreadyUsed) {
      const usedAtText = qrCode.usedAt && typeof qrCode.usedAt === 'string' 
        ? format(parseISO(qrCode.usedAt), 'dd.MM.yyyy HH:mm', { locale: de }) 
        : 'unbekannt';
      
      return (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            QR-Code bereits verwendet
          </Typography>
          <Typography variant="body2">
            Dieser QR-Code wurde bereits am {usedAtText} verarbeitet.
            {qrCode.usedBy && ` Bearbeitet von: ${qrCode.usedBy}`}
          </Typography>
        </Alert>
      );
    }

    if (scanResult.expired) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            <ErrorIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            QR-Code abgelaufen
          </Typography>
          <Typography variant="body2">
            Dieser QR-Code ist am {qrCode.expiresAt && typeof qrCode.expiresAt === 'string' ? format(parseISO(qrCode.expiresAt), 'dd.MM.yyyy HH:mm', { locale: de }) : 'unbekannt'} abgelaufen.
          </Typography>
        </Alert>
      );
    }

    return (
      <Card sx={{ mb: 2, border: 2, borderColor: 'success.main' }}>
        <CardContent>
          <Typography variant="h5" color="success.main" sx={{ mb: 2 }}>
            <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Gültiger QR-Code
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Freiwilliger
              </Typography>
              <List dense>
                <ListItem disablePadding>
                  <ListItemText
                    primary="Name"
                    secondary={volunteer.name}
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemText
                    primary="E-Mail"
                    secondary={volunteer.email}
                  />
                </ListItem>
                {volunteer.phoneNumber && (
                  <ListItem disablePadding>
                    <ListItemText
                      primary="Telefon"
                      secondary={volunteer.phoneNumber}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Berechtigung
              </Typography>
              <List dense>
                <ListItem disablePadding>
                  <ListItemText
                    primary="Arbeitsstunden"
                    secondary={
                      <Chip
                        label={`${qrPayload.totalWorkingHours}h`}
                        color="primary"
                        icon={<AccessTimeIcon />}
                        size="small"
                      />
                    }
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemText
                    primary="Eintritt"
                    secondary={
                      <Box>
                        <Chip
                          label="Kostenloser Eintritt"
                          color="success"
                          icon={<PersonIcon />}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        {qrPayload.includesPartnerEntry && (
                          <Chip
                            label="+ Partner"
                            color="success"
                            icon={<PeopleIcon />}
                            size="small"
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemText
                    primary="Erstellt am"
                    secondary={qrPayload.createdAt && typeof qrPayload.createdAt === 'string' ? format(parseISO(qrPayload.createdAt), 'dd.MM.yyyy HH:mm', { locale: de }) : 'Unbekannt'}
                  />
                </ListItem>
                {qrPayload.expiresAt && (
                  <ListItem disablePadding>
                    <ListItemText
                      primary="Gültig bis"
                      secondary={qrPayload.expiresAt && typeof qrPayload.expiresAt === 'string' ? format(parseISO(qrPayload.expiresAt), 'dd.MM.yyyy HH:mm', { locale: de }) : 'Unbekannt'}
                    />
                  </ListItem>
                )}
                {qrPayload.comment && (
                  <ListItem disablePadding>
                    <ListItemText
                      primary="Kommentar"
                      secondary={qrPayload.comment}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          </Box>
          
          {/* Show comment prominently if available */}
          {qrPayload.comment && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Kommentar:</strong> {qrPayload.comment}
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          QR-Code Scanner
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Scannen Sie QR-Codes für den kostenlosen Eintritt
        </Typography>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Camera Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          {!isScanning && !scanResult ? (
            <Box sx={{ textAlign: 'center' }}>
              <QrCodeScannerIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>
                QR-Code scannen
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<CameraAltIcon />}
                  onClick={startScanning}
                  size="large"
                >
                  Kamera starten
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setShowManualInput(!showManualInput)}
                  size="large"
                >
                  Manuell eingeben
                </Button>
              </Box>
            </Box>
          ) : isScanning ? (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                QR-Code vor die Kamera halten
              </Typography>
              <Box sx={{ position: 'relative', maxWidth: 400, mx: 'auto', mb: 2 }}>
                <video
                  ref={videoRef}
                  autoPlay={true}
                  playsInline={true}
                  muted={true}
                  controls={false}
                  style={{
                    width: '100%',
                    height: 'auto',
                    minHeight: '200px',
                    borderRadius: theme.shape.borderRadius,
                    border: `2px solid ${theme.palette.primary.main}`,
                    backgroundColor: '#000',
                    objectFit: 'cover',
                  }}
                />
                <canvas
                  ref={canvasRef}
                  style={{ display: 'none' }}
                />
                {processing && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(0,0,0,0.5)',
                      borderRadius: theme.shape.borderRadius,
                    }}
                  >
                    <CircularProgress color="primary" />
                  </Box>
                )}
              </Box>
              <Button
                variant="outlined"
                startIcon={<CloseIcon />}
                onClick={stopScanning}
              >
                Scannen beenden
              </Button>
            </Box>
          ) : null}

          {/* Manual Input */}
          {showManualInput && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>
                QR-Code manuell eingeben
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="QR-Code Daten"
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  placeholder="QR-Code Inhalt hier einfügen..."
                  multiline
                  rows={3}
                />
                <Button
                  variant="contained"
                  onClick={handleManualInput}
                  disabled={!manualInput.trim() || processing}
                  sx={{ mt: 1 }}
                >
                  {processing ? <CircularProgress size={20} /> : 'Scannen'}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>

        {/* Scan Result */}
        {renderScanResult()}

        {/* Reset Button */}
        {(scanResult || showManualInput) && (
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={resetScanner}
              size="large"
            >
              Neuen Scan starten
            </Button>
          </Box>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {scanResult?.alreadyUsed ? 'QR-Code Information' : 'QR-Code Validation'}
        </DialogTitle>
        <DialogContent>
          {scanResult?.qrCode && scanResult?.volunteer && (
            <Box>
              {scanResult.alreadyUsed ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Dieser QR-Code wurde bereits verwendet
                  </Typography>
                  {scanResult.qrCode.usedAt && typeof scanResult.qrCode.usedAt === 'string' && (
                    <Typography variant="body2">
                      Am: {format(parseISO(scanResult.qrCode.usedAt), 'dd.MM.yyyy HH:mm', { locale: de })}
                      {scanResult.qrCode.usedBy && ` | Bearbeitet von: ${scanResult.qrCode.usedBy}`}
                    </Typography>
                  )}
                </Alert>
              ) : scanResult.expired ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Dieser QR-Code ist abgelaufen
                  </Typography>
                  {scanResult.qrCode.expiresAt && typeof scanResult.qrCode.expiresAt === 'string' && (
                    <Typography variant="body2">
                      Abgelaufen am: {format(parseISO(scanResult.qrCode.expiresAt), 'dd.MM.yyyy HH:mm', { locale: de })}
                    </Typography>
                  )}
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  QR-Code für Eintritt validieren und als verwendet markieren?
                </Alert>
              )}
              
              <Typography variant="h6" sx={{ mb: 1 }}>
                {scanResult.volunteer.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {JSON.parse(scanResult.qrCode.qrCodePayload).totalWorkingHours}h Arbeitsstunden
                {JSON.parse(scanResult.qrCode.qrCodePayload).includesPartnerEntry && ' (+ Partner)'}
              </Typography>
              
              {/* Show comment if available */}
              {(() => {
                const payload = JSON.parse(scanResult.qrCode.qrCodePayload);
                return payload.comment ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Kommentar:</strong> {payload.comment}
                    </Typography>
                  </Alert>
                ) : null;
              })()}
              
              {/* Show additional volunteer information */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Freiwilliger-Details:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  E-Mail: {scanResult.volunteer.email}
                </Typography>
                {scanResult.volunteer.phoneNumber && (
                  <Typography variant="body2" color="text.secondary">
                    Telefon: {scanResult.volunteer.phoneNumber}
                  </Typography>
                )}
              </Box>

              {/* Show QR code details */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  QR-Code Details:
                </Typography>
                {(() => {
                  const payload = JSON.parse(scanResult.qrCode.qrCodePayload);
                  return (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        Erstellt am: {payload.createdAt && typeof payload.createdAt === 'string' ? format(parseISO(payload.createdAt), 'dd.MM.yyyy HH:mm', { locale: de }) : 'Unbekannt'}
                      </Typography>
                      {payload.expiresAt && (
                        <Typography variant="body2" color="text.secondary">
                          Gültig bis: {format(parseISO(payload.expiresAt), 'dd.MM.yyyy HH:mm', { locale: de })}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        Partner-Eintritt: {payload.includesPartnerEntry ? 'Ja' : 'Nein'}
                      </Typography>
                      {payload.comment && (
                        <Typography variant="body2" color="text.secondary">
                          Kommentar: {payload.comment}
                        </Typography>
                      )}
                    </>
                  );
                })()}
              </Box>
              
              {/* Only show current user info for unused codes */}
              {!scanResult.alreadyUsed && !scanResult.expired && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Bearbeitet von Personal:</strong> {volunteer?.name || 'Unbekannt'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', mt: 0.5 }}>
                    Diese Person wird als verarbeitende Stelle gespeichert
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            {scanResult?.alreadyUsed || scanResult?.expired ? 'Schließen' : 'Abbrechen'}
          </Button>
          {/* Only show mark as used button for unused and non-expired codes */}
          {!scanResult?.alreadyUsed && !scanResult?.expired && (
            <Button
              onClick={handleMarkAsUsed}
              variant="contained"
              color="success"
              disabled={!volunteer?.name?.trim() || markingAsUsed}
              startIcon={markingAsUsed ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            >
              {markingAsUsed ? 'Wird bearbeitet...' : 'Eintritt gewähren'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default QRCodeScannerPage;
